import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createScanJob,
  getScanJob,
  type ScanJobResult,
  type ScanUnits,
} from '@/services/scanToCad';

const POLL_MS = 1500;
// After a job reports 'failure' the status op issues the (idempotent) token
// refund. Keep polling a few more times so a transient, server-side-swallowed
// refund failure gets re-attempted — refund_tokens no-ops once the ledger row
// is flipped, so extra failure polls are harmless.
const MAX_FAILURE_REFUND_POLLS = 3;

export interface UseScanJob {
  /** null until a job is running; otherwise the latest polled state. */
  job: ScanJobResult | null;
  /** The recon job id — needed to open an editing session (workbench P2). */
  jobId: string | null;
  running: boolean;
  error: string | null;
  /** Kick off a reconstruction for an uploaded scan. */
  start: (meshRef: string, units: ScanUnits) => Promise<void>;
  reset: () => void;
}

/**
 * Drives a scan→STEP reconstruction job: creates it, then polls for REAL
 * progress (the recon pipeline's stage + overall fraction) until it reaches a
 * terminal state. The polled `progress`/`stage` are what the progress bar
 * renders — a true reflection of pipeline state, not a timer.
 */
export function useScanJob(): UseScanJob {
  const [job, setJob] = useState<ScanJobResult | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);
  const failurePolls = useRef(0);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const poll = useCallback(async (jobId: string) => {
    try {
      const next = await getScanJob(jobId);
      if (cancelled.current) return;
      setJob(next);
      if (next.status === 'success') {
        setRunning(false);
        return;
      }
      if (next.status === 'failure') {
        setError(next.error ?? 'Reconstruction failed.');
        // A few extra polls so a dropped server-side refund is re-issued (safe:
        // the refund is idempotent). The UI already shows the failure/error.
        if (failurePolls.current < MAX_FAILURE_REFUND_POLLS) {
          failurePolls.current += 1;
          timer.current = setTimeout(() => poll(jobId), POLL_MS);
        } else {
          setRunning(false);
        }
        return;
      }
      timer.current = setTimeout(() => poll(jobId), POLL_MS);
    } catch (e) {
      if (cancelled.current) return;
      setRunning(false);
      setError(e instanceof Error ? e.message : 'Reconstruction failed.');
    }
  }, []);

  const start = useCallback(
    async (meshRef: string, units: ScanUnits) => {
      clearTimer();
      cancelled.current = false;
      failurePolls.current = 0;
      setError(null);
      setRunning(true);
      setJob({ status: 'queued', progress: 0, stage: null });
      try {
        const { jobId: id } = await createScanJob({ meshRef, units });
        if (cancelled.current) return;
        setJobId(id);
        void poll(id);
      } catch (e) {
        setRunning(false);
        setError(
          e instanceof Error ? e.message : 'Could not start reconstruction.',
        );
      }
    },
    [poll],
  );

  const reset = useCallback(() => {
    cancelled.current = true;
    clearTimer();
    setJob(null);
    setJobId(null);
    setRunning(false);
    setError(null);
  }, []);

  // stop polling on unmount
  useEffect(() => {
    return () => {
      cancelled.current = true;
      clearTimer();
    };
  }, []);

  return { job, jobId, running, error, start, reset };
}
